import { Dialog, DialogContent } from '@/components/ui/dialog';
import { clsx } from 'clsx';
import EmailSummary from './EmailSummary';
import { buildIncidentEmailHTML, Incident, Communication } from './EmailGeneration';
import { addIncidentUpdate } from '@/api/fetchIncidents'; // NEW
import React from 'react';

interface ClosingDialogProps {
  open: boolean;
  step: 'confirm' | 'notes' | 'preview' | 'done';
  onOpenChange: (o: boolean) => void;
  closingNotes: string;
  setClosingNotes: (v: string) => void;
  closingRephrased: string;
  setClosingRephrased: (v: string) => void;
  closingRephrasing: boolean;
  startRephrase: () => void;
  proceedToNotes: () => void;
  proceedToPreview: () => void;
  backFromNotes: () => void;
  backFromPreview: () => void;
  cancelAll: () => void;
  sendClosureEmail: () => void;
  canSend: boolean;
  sending: boolean;
  openClosureEmailPreview: () => void;
  hasRecipients: boolean;
  currentIncident: Incident | null;
  currentImpactedApps: string[];
  currentComms: Communication[];
  execSummary: string;
  setExecSummary: React.Dispatch<React.SetStateAction<string>>;
  generateExecutiveSummary: () => void;
  loadingExecSummary: boolean;
  emailRecipients: string[];
  setEmailRecipients: React.Dispatch<React.SetStateAction<string[]>>;
  emailInput: string;
  setEmailInput: React.Dispatch<React.SetStateAction<string>>;
  emailSubject: string;
  setEmailSubject: (v: string) => void;
  emailSending: boolean;
  emailSent: boolean;
  setEmailSent: (v: boolean) => void;
  emailHtml: string;
  setEmailHtml: (v: string) => void;
  setPreviewOpen: (v: boolean) => void;
  userRole: string;
  userEmail: string;
  onApprovalSubmitted?: (c: Communication) => void; // NEW: notify parent to append the pending approval update to the feed
  onIncidentCircumvented?: (incidentId: string) => void; // NEW: notify parent that incident is now circumvented
}

export function CircumventedDialog(props: ClosingDialogProps) {
  const {
    open, step, onOpenChange,
    closingNotes, setClosingNotes,
    closingRephrased, setClosingRephrased,
    closingRephrasing, startRephrase,
    proceedToNotes, proceedToPreview,
    backFromNotes, backFromPreview,
    cancelAll, sendClosureEmail,
    canSend, sending, hasRecipients,
    currentIncident, currentImpactedApps, currentComms,
    execSummary, setExecSummary, generateExecutiveSummary,
    loadingExecSummary, emailRecipients, setEmailRecipients,
    emailInput, setEmailInput, emailSubject, setEmailSubject,
    emailSending, emailSent, setEmailSent,
    emailHtml, setEmailHtml, setPreviewOpen, userRole, userEmail
  } = props;

  const enhancedSendClosureEmail = () => {
    if (!currentIncident) return;
    const html = buildIncidentEmailHTML(
      currentIncident,
      currentImpactedApps,
      currentComms,
      execSummary,
      (closingRephrased || closingNotes).trim()
    );
    setEmailHtml(html); // ensure preview state updated
    sendClosureEmail(); // existing send logic (assumes it reads emailHtml or builds itself)
  };

  // NEW: local submitting state for user flow
  const [submittingForApproval, setSubmittingForApproval] = React.useState(false);
  const [approvalMessage, setApprovalMessage] = React.useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[92vw] p-4 flex flex-col gap-4">
        {step === 'confirm' && (
          <div className="text-[12px] space-y-4">
            <p>Close further updates? You can still add final notes and send a circumvented email.</p>
            <div className="flex justify-end gap-2">
              <button onClick={cancelAll} className="text-[11px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/60">
                Cancel
              </button>
              <button onClick={proceedToNotes} className="text-[11px] px-3 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F]">
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'notes' && (
          <div className="flex flex-col gap-3">
            <textarea
              rows={6}
              value={closingNotes}
              onChange={(e) => {
                setClosingNotes(e.target.value);
                if (closingRephrased) setClosingRephrased('');
              }}
              placeholder="Enter closing notes (resolution, root cause, next steps)…"
              className="w-full resize-none rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 text-[12px] p-2 leading-[1.25]"
            />
            <div className="flex items-center justify-between">
              <button
                onClick={backFromNotes}
                className="text-[11px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/60"
              >
                Back
              </button>
              <div className="flex gap-2">
                <button
                  onClick={startRephrase}
                  disabled={!closingNotes.trim() || closingRephrasing}
                  className="text-[11px] px-3 py-1 rounded-md border border-[#18E78F]/40 hover:bg-[#18E78F]/15 disabled:opacity-40"
                >
                  {closingRephrasing ? 'Rephrasing…' : (closingRephrased ? 'Rephrase Again' : 'Rephrase')}
                </button>
                <button
                  onClick={async () => {
                    if (!closingNotes.trim()) return;
                    const finalNotes = (closingRephrased || closingNotes).trim();

                    // If regular user, submit circumvented update for admin approval and skip preview/email
                    if (userRole === 'incident_comms_user') {
                      if (!currentIncident?.id) return;
                      try {
                        setSubmittingForApproval(true);
                        setApprovalMessage(null);

                        const result = await addIncidentUpdate(
                          currentIncident.id,
                          finalNotes,
                          userEmail,           // updated_by (optional)
                          true,                // circumvented
                          userRole             // role
                        );

                        // Inform parent to add a local communication entry (pending approval)
                        if (result?.inserted && currentIncident && props.onApprovalSubmitted) {
                          const newComm: Communication = {
                            id: `upd-${result.inserted.update_id}`,
                            incidentId: currentIncident.id,
                            author: result.inserted.updated_by || userEmail || 'analyst',
                            role: 'analyst',
                            timestamp: result.inserted.timestamp || new Date().toISOString(),
                            content: result.inserted.update,
                            update_id: result.inserted.update_id, 
                            iscircumvented: true,
                            iscommunication_sent: false,
                            approved: false,
                            approval_id: result.pending_approval?.approval_id
                          };

                          props.onApprovalSubmitted(newComm);
                          
                          // ✅ NEW: Notify parent that incident is now circumvented
                          if (result.incident_circumvented && props.onIncidentCircumvented) {
                            props.onIncidentCircumvented(currentIncident.id);
                          }
                        }

                        // Close dialog - give it a small delay to ensure state updates propagate
                        setTimeout(() => {
                          setPreviewOpen(false);
                          onOpenChange(false);
                        }, 100);
                      } catch (e) {
                        console.error('Failed to submit for approval', e);
                        setApprovalMessage('Failed to submit for admin approval. Please try again.');
                      } finally {
                        setSubmittingForApproval(false);
                      }
                      return;
                    }

                    // Admin or elevated roles continue to preview/email
                    const severity = currentIncident?.severity?.toUpperCase() || '';
                    const forcedHeader = `Current Status: CIRCUMVENTED | Severity: ${severity}`;
                    setExecSummary((prev: string) => {
                      let next = prev;
                      if (/^Current Status:/m.test(next)) {
                        next = next.replace(/^Current Status:.*$/m, forcedHeader);
                      } else {
                        next = `${forcedHeader}\n\n${next.trim()}`;
                      }
                      const marker = 'Closing Notes:';
                      if (next.includes(finalNotes)) return next;
                      if (next.includes(marker)) {
                        next = next.replace(new RegExp(`${marker}[\\s\\S]*$`, 'i'), `${marker}\n${finalNotes}`);
                      } else {
                        next = `${next.trim()}\n\n${marker}\n${finalNotes}`;
                      }
                      return next;
                    });
                    proceedToPreview();
                  }}
                  disabled={!closingNotes.trim() || submittingForApproval}
                  className="text-[11px] px-3 py-1 rounded-md bg-[#18E78F]/90 text-black font-semibold hover:bg-[#18E78F] disabled:opacity-40"
                >
                  {userRole === 'incident_comms_user'
                    ? (submittingForApproval ? 'Submitting…' : 'Submit for Approval')
                    : 'Continue'}
                </button>
              </div>
            </div>
            {closingRephrased && (
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Rephrased
                </label>
                <textarea
                  rows={6}
                  value={closingRephrased}
                  onChange={(e) => setClosingRephrased(e.target.value)}
                  className="w-full resize-vertical rounded-md bg-background/60 dark:bg-background/40 border border-border/50 focus:outline-none focus:ring-1 focus:ring-[#18E78F]/50 text-[11px] p-2 leading-[1.25]"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setClosingRephrased('')}
                    className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-background/60"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => setClosingRephrased(closingNotes)}
                    disabled={!closingNotes.trim()}
                    className="text-[10px] px-2 py-1 rounded-md border border-border/50 hover:bg-background/60 disabled:opacity-40"
                  >
                    Reset to Original
                  </button>
                </div>
              </div>
            )}
            {userRole === 'incident_comms_user' && approvalMessage && (
              <p className={clsx('text-[10px]', approvalMessage.includes('Failed') ? 'text-red-500' : 'text-muted-foreground')}>
                {approvalMessage}
              </p>
            )}
          </div>
        )}

        {step === 'preview' && userRole !== 'incident_comms_user' && (
          <div className="flex flex-col gap-4">
            <EmailSummary
              currentIncident={currentIncident as Incident}
              currentImpactedApps={currentImpactedApps}
              currentComms={currentComms}
              execSummary={execSummary}
              setExecSummary={setExecSummary}
              generateExecutiveSummary={generateExecutiveSummary}
              loadingExecSummary={loadingExecSummary}
              emailRecipients={emailRecipients.map((addr) => ({ email: addr }))}
              setEmailRecipients={(recipients: React.SetStateAction<{ email: string }[]>) => {
                if (typeof recipients === 'function') {
                  setEmailRecipients((prev) =>
                    (recipients(prev.map((addr) => ({ email: addr }))) as { email: string }[]).map(
                      (r: { email: string }) => r.email
                    )
                  );
                } else {
                  setEmailRecipients(recipients.map((r: { email: string }) => r.email));
                }
              }}
              emailInput={emailInput}
              setEmailInput={setEmailInput}
              emailSubject={emailSubject}
              setEmailSubject={setEmailSubject}
              emailSending={sending}
              emailSent={emailSent}
              setEmailSent={setEmailSent}
              sendEmail={enhancedSendClosureEmail}
              emailHtml={emailHtml}
              setEmailHtml={setEmailHtml}
              setPreviewOpen={setPreviewOpen}
              close={backFromPreview}
              finalOriginalNotes={closingNotes}
              finalRephrasedNotes={closingRephrased}
              useRephrased={!!closingRephrased} // default to rephrased if exists
              setUseRephrased={(v) => {
                // toggle chosen version (if user switches to original clear rephrased usage flag)
                if (!v) setClosingRephrased(closingRephrased); // no change; selection handled when building email upstream
              }}
              hideHeaderClose
              sendLabel="Send Final Email"
              sendingOverride={sending}
            />
            {!hasRecipients && (
              <p className="text-[10px] text-red-500">
                No recipients added. Add recipients above before sending.
              </p>
            )}
            <div className="flex justify-end">
              <button
                onClick={backFromPreview}
                className="text-[11px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/60"
              >
                Back
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground/60">
              Final email will include prior updates plus these closing notes (shown in the summary editor).
            </p>
          </div>
        )}

        {step === 'done' && (
          <div className="text-[12px]">
            <p>
              {userRole === 'incident_comms_user'
                ? (approvalMessage || 'Update submitted for admin approval.')
                : 'Circumvented email sent. Updates locked.'}
            </p>
            <div className="flex justify-end mt-3">
              <button
                onClick={() => onOpenChange(false)}
                className="text-[11px] px-3 py-1 rounded-md border border-border/50 hover:bg-background/60"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CircumventedDialog;